<script>
import {useEvaluationPerformanceCandidateStore} from "@/stores/Evaluation/EvaluationPerformanceCandidate.js"
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import {ref, watch} from "vue";
import {useSecurityStore} from "@/stores/Security.js";
import axios from "axios";
import {useEvaluationInterviewStore} from "@/stores/Evaluation/EvaluationInterview";
import {storeToRefs} from "pinia";

export default {
    props: {},
    setup() {
        const {
            total,
            loading,
            filter,
        } = useSharedData();

        const {transformFilterValues} = useSharedMethod();
        const {formatedDate, formatedEvaluationCycle} = useSharedMethod();
        const {$i18n, $route} = useTools();

        const evaluationPerformanceCandidateStore = useEvaluationPerformanceCandidateStore();
        const securityStore = useSecurityStore();

        filter.value = {archive: false};

        const dynamicStepHeaders = ref([]);
        const headers = ref([]);

        function findAll() {
            if (performanceId.value) {
                loading.value = true;
                filter.value.performanceId = performanceId.value;

                headers.value = [];
                dynamicStepHeaders.value = [];

                addHeaderHead();
                axios.get('/evaluationPerformanceCandidates', {params: transformFilterValues(filter.value)}).then((response) => {
                    evaluationPerformanceCandidateStore.setEvaluationPerformanceCandidates(formatData(response.data));
                    total.value = evaluationPerformanceCandidateStore.evaluationPerformanceCandidates.length;

                    evaluationPerformanceCandidateStore.evaluationPerformanceCandidates.forEach(element => {
                        element.steps.forEach(step => {
                            element[step.stepPhaseName] = step;
                            addHeader(step);
                        });
                    });
                    dynamicStepHeaders.value.sort((a, b) => a.ordre - b.ordre);
                    headers.value.push(...dynamicStepHeaders.value);
                    addFooterHeader();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function addHeaderHead() {
            headers.value.push(
                {
                    text: $i18n.t("module.worker"),
                    align: "left",
                    sortable: true,
                    value: "workerName",
                    width: "0%",
                    groupable: true,
                },
            );
        }

        function addHeader(step) {
            if (!dynamicStepHeaders.value.some((header) => header.text === step.stepPhaseName)) {
                dynamicStepHeaders.value.push({
                    text: step.stepPhaseName,
                    align: "center",
                    sortable: false,
                    value: step.stepPhaseName,
                    ordre: step.stepOrder + 1,
                });
            }
        }

        function addFooterHeader() {
            headers.value.push(
                {
                    text: $i18n.t('evaluation.score'),
                    align: "left",
                    sortable: true,
                    value: "note",
                    groupable: false,
                },
            );
        }

        function formatData(data) {
            const grouped = data.reduce((acc, item) => {
                if (!acc[item.workerId]) {
                    acc[item.workerId] = [];
                }
                acc[item.workerId].push(item);
                return acc;
            }, {});
            // Map to desired structure
            return Object.entries(grouped).map(([workerId, items]) => {
                // Find the item with max stepOrder
                const lastStep = items.reduce((max, item) =>
                    item.stepOrder > max.stepOrder ? item : max
                );

                return {
                    workerId: parseInt(workerId),
                    workerName: items[0].workerName,
                    note: lastStep.interviewNote,
                    steps: items.map(item => ({
                        id: item.id,
                        stepId: item.stepId,
                        stepPhaseName: item.stepPhaseName,
                        stepOrder: item.stepOrder,
                        interviewId: item.interviewId,
                        interviewNote: item.interviewNote,
                        done: item.done,
                        tolerated: item.tolerated,
                        launched: item.launched,
                        current: item.current,
                        stepLaunched: item.stepLaunched,
                        stepDone: item.stepDone,
                        stepCurrent: item.stepCurrent,
                        interviewClosed: item.interviewClosed
                    }))
                };
            });
        }

        const performance = ref({});
        const performanceId = ref($route.params.performanceId);
        const loadingPerformance = ref(false);

        function findPerformanceById() {
            if (performanceId.value !== null && performanceId.value !== undefined) {
                loadingPerformance.value = true;
                axios.get(`/evaluationPerformances/${performanceId.value}`).then((response) => {
                    performance.value = response.data;
                }).finally(() => {
                    loadingPerformance.value = false;
                });
            }
        }

        const evaluationInterviewStore = useEvaluationInterviewStore();
        const {refresh} = storeToRefs(evaluationInterviewStore);

        watch(() => refresh.value, () => {
            findAll();
            findPerformanceById();
        });

        watch(() => filter.value, () => {
            findAll();
        }, {deep: true});

        watch(() => performanceId.value, () => {
            findAll();
            findPerformanceById();
        }, {immediate: true});

        function formatedState(item) {
            if (item.tolerated) {
                return $i18n.t('evaluation.tolerated');
            } else if (item.done) {
                return `${$i18n.t('common.validated')} (${item.interviewNote})`;
            } else if (item.current) {
                return `${$i18n.t('common.ACTION_STATE_IN_PROGRESS')} (${item.interviewNote})`;
            } else {
                return $i18n.t('common.ACTION_STATE_WAITING');
            }
        }

        return {
            evaluationPerformanceCandidateStore,
            securityStore,
            loadingPerformance,
            performanceId,
            performance,
            headers,
            dynamicStepHeaders,
            total,
            loading,
            filter,
            findAll,
            formatedDate,
            formatedState,
            formatedEvaluationCycle,
        };
    },
};
</script>

<template>
    <div>
        <w-app
            :title="$t('module.evaluation_performance_candidates')"
            :total="total"
            show-filter
            :filter="filter"
        >
            <template #filter>
                <v-row dense>
                    <v-col cols="12">
                        <str-worker-autocomplete
                            v-model="filter.workerId"
                            :label="$t('module.worker')"
                        />
                    </v-col>
                    <v-col cols="12">
                        <str-evaluation-file-autocomplete
                            v-model="filter.fileId"
                            :label="'Compagne'"
                            type="EVALUATION_PERFORMANCE"
                        />
                    </v-col>
                </v-row>
            </template>
            <template v-slot:rightToolbar>
                <w-btn-reload
                    :loading="loading"
                    @press="findAll"
                />
            </template>

            <template v-slot:content>
                <v-container fluid>
                    <v-row dense>
                        <v-col cols="3">
                            <str-performance-autocomplete
                                v-model="performanceId"
                                :label="$t('evaluation.companion')"
                                :select-max="!$route.params.performanceId"
                                not-clearable
                            />
                        </v-col>
                    </v-row>
                </v-container>
                <v-row dense>
                    <v-col cols="3">
                        <v-skeleton-loader
                            v-show="loadingPerformance"
                            ref="skeleton"
                            :type="'table'"
                            class="mx-auto"
                        ></v-skeleton-loader>
                        <v-simple-table
                            v-if="performance && !loadingPerformance"
                            class="elevation-5"
                            height="600"
                        >
                            <template v-slot:default>
                                <tbody>
                                <tr>
                                    <th class="text-right">{{ $t('common.code') }}</th>
                                    <td class="text-left">
                                        <w-chip :text="performance.code"/>
                                    </td>
                                </tr>
                                <tr>
                                    <th class="text-right">{{ $t('common.label') }}</th>
                                    <td class="text-left">{{ performance.name }}</td>
                                </tr>
                                <tr>
                                    <th class="text-right">{{ $t('common.start') }}</th>
                                    <td class="text-left">
                                        <w-inline-text outlined :text="formatedDate(performance.cycleStart)"/>
                                    </td>
                                </tr>
                                <tr>
                                    <th class="text-right">{{ $t('common.end') }}</th>
                                    <td class="text-left">
                                        <w-chip color="teal" :text="formatedDate(performance.cycleEnd)"/>
                                    </td>
                                </tr>
                                <tr>
                                    <th class="text-right">{{ $t('common.period') }}</th>
                                    <td class="text-left">
                                        <w-inline-text :text="formatedEvaluationCycle(performance)"/>
                                    </td>
                                </tr>
                                <!--<tr
                                    v-for="step in performance.steps"
                                    :key="step.id"
                                >
                                    <th class="text-right">{{ step.phaseName }}</th>
                                    <td class="text-left">
                                        <w-chip
                                            outlined
                                            :text="`${step.completedNumber + step.toleratedNumber}/${step.concernedNumber}`"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th class="text-right">{{ $t('common.percent') }}</th>
                                    <td class="text-left">
                                        <v-progress-linear
                                            :value="((performance.completedNumber + performance.toleratedNumber) * 100.0)/performance.concernedNumber"
                                            color="teal lighten-2"
                                            buffer-value="0"
                                            stream
                                            height="25"
                                        >
                                            <strong>
                                                {{ (((performance.completedNumber + performance.toleratedNumber) * 100.0) / performance.concernedNumber).toFixed(2) }}%</strong>
                                        </v-progress-linear>
                                    </td>
                                </tr>-->
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-col>
                    <v-col cols="9">
                        <w-data-table
                            :headers="headers"
                            :items="evaluationPerformanceCandidateStore.evaluationPerformanceCandidates"
                            :total="total"
                            identity="workerId"
                            :loading="loading"
                            disable-pagination
                            fixed-header
                            height="600"
                            descending
                        >
                            <template v-slot:[`item.workerName`]="{ item }">
                                <w-inline-text bold :text="item.workerName"/>
                            </template>

                            <!-- Dynamic slot for step headers -->
                            <template
                                v-for="header in dynamicStepHeaders"
                                v-slot:[`item.${header.value}`]="{ item }"
                            >
                                <w-inline-text
                                    :key="header.value"
                                    :text="formatedState(item[header.value])"
                                />
                                <w-btn-icon
                                    :key="`evaluate-${header.value}`"
                                    :disabled="!item[header.value].launched"
                                    color="blue darken-2"
                                    icon="mdi-play-circle-outline"
                                    tooltip="Évaluer"
                                    @press="$router.push({name: 'evaluation_performance_candidates.interviews.edit', params: {id: item[header.value].interviewId}})"
                                />
                                <w-btn-icon
                                    :key="`tolerate-${header.value}`"
                                    :disabled="item[header.value].done || item[header.value].tolerated || !item[header.value].launched"
                                    color="purple"
                                    icon="mdi-calendar-clock"
                                    tooltip="Reporter"
                                    @press="$router.push({name: 'evaluation_performance_candidates.tolerate', params: {id: item[header.value].id}})"
                                />

                            </template>
                            <template v-slot:[`item.note`]="{ item }">
                                <w-chip color="teal" :text="item.note"/>
                            </template>
                        </w-data-table>
                    </v-col>
                </v-row>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>
